import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import "./SweepstakesComplianceChecklist.css";

const checklistSchema = {
  "@type": "ItemList",
  name: "Sweepstakes compliance checklist",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Eligibility and geographic limits" },
    { "@type": "ListItem", position: 2, name: "No purchase necessary + AMOE details" },
    { "@type": "ListItem", position: 3, name: "Start/end dates and entry limits" },
    { "@type": "ListItem", position: 4, name: "Prize description and ARV" },
    { "@type": "ListItem", position: 5, name: "Odds of winning and selection method" },
    { "@type": "ListItem", position: 6, name: "Winner notification timeline" },
    { "@type": "ListItem", position: 7, name: "Sponsor/administrator details" },
    { "@type": "ListItem", position: 8, name: "Privacy and data use" },
    { "@type": "ListItem", position: 9, name: "Tax and release requirements" },
    { "@type": "ListItem", position: 10, name: "Recordkeeping and audit trail" },
  ],
};

const faqSchema = {
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is “no purchase necessary” required for sweepstakes?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Many jurisdictions require a free Alternate Method of Entry (AMOE). Always confirm requirements with legal counsel for your region.",
      },
    },
    {
      "@type": "Question",
      name: "What should official rules include?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Official rules typically cover eligibility, entry methods, prize details, odds, start/end dates, winner selection, and sponsor information.",
      },
    },
    {
      "@type": "Question",
      name: "How long should sweepstakes records be kept?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Retention requirements vary by jurisdiction and policy. Keep entrant and winner records long enough to support audits and inquiries.",
      },
    },
  ],
};

const SweepstakesComplianceChecklist = () => {
  return (
    <main className="compliance-page">
      <Seo
        title="Sweepstakes Compliance Checklist | Clean Sweeps"
        description="A sweepstakes compliance checklist covering AMOE, official rules, eligibility, and recordkeeping for Shopify sweepstakes."
        path="/sweepstakes-compliance-checklist"
        image="/og/sweepstakes-compliance.png"
        imageAlt="Sweepstakes compliance checklist"
        schema={[checklistSchema, faqSchema]}
      />

      <header className="compliance-page__hero">
        <p className="compliance-page__eyebrow">Compliance checklist</p>
        <h1>Sweepstakes compliance checklist for Shopify.</h1>
        <p className="compliance-page__lead">
          Use this sweepstakes compliance checklist to cover eligibility, AMOE,
          official rules, and recordkeeping. Clean Sweeps helps manage entry
          workflows, but confirm legal requirements for your region.
        </p>
        <div className="compliance-page__actions">
          <a
            href="https://apps.shopify.com/clean-sweeps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            Install Clean Sweeps
          </a>
          <Link to="/resources" className="btn btn--ghost">
            Browse resources
          </Link>
        </div>
      </header>

      <section className="compliance-page__section">
        <div className="compliance-page__header">
          <h2>Core compliance checklist</h2>
          <p>
            A sweepstakes platform should help you document these essentials.
          </p>
        </div>
        <ul className="compliance-page__checklist">
          <li>Eligibility criteria and geographic restrictions.</li>
          <li>No purchase necessary statement and AMOE instructions.</li>
          <li>Start/end dates, entry limits, and time zone.</li>
          <li>Prize description, approximate retail value (ARV), and quantity.</li>
          <li>Odds of winning and selection method.</li>
          <li>Winner notification timeline and verification steps.</li>
          <li>Sponsor/administrator contact details.</li>
          <li>Privacy policy and data usage disclosures.</li>
          <li>Tax reporting, releases, and publicity permissions.</li>
          <li>Recordkeeping and audit trail procedures.</li>
        </ul>
        <p className="compliance-page__note">
          This checklist is not legal advice. Always consult legal counsel.
        </p>
      </section>

      <section className="compliance-page__section">
        <div className="compliance-page__header">
          <h2>AMOE essentials</h2>
          <p>
            AMOE (Alternate Method of Entry) keeps sweepstakes compliant when
            purchase is a primary entry path.
          </p>
        </div>
        <div className="compliance-page__grid">
          <article className="compliance-page__card">
            <h3>Equal odds</h3>
            <p>
              AMOE entries should have the same chance of winning as
              purchase-based entries.
            </p>
          </article>
          <article className="compliance-page__card">
            <h3>Clear instructions</h3>
            <p>
              Provide a visible AMOE link, form, or mail-in instructions in
              your official rules.
            </p>
          </article>
          <article className="compliance-page__card">
            <h3>Matching timelines</h3>
            <p>
              Align AMOE entry windows with promotion dates and deadlines.
            </p>
          </article>
        </div>
      </section>

      <section className="compliance-page__section">
        <div className="compliance-page__header">
          <h2>Official rules essentials</h2>
          <p>
            Clear rules protect your brand and set expectations for
            participants.
          </p>
        </div>
        <ul className="compliance-page__list">
          <li>Eligibility, age, and residency requirements.</li>
          <li>Entry methods, limits, and bonus entries.</li>
          <li>Prize descriptions and odds of winning.</li>
          <li>Winner selection and notification procedures.</li>
          <li>Release, publicity, and tax disclosure language.</li>
        </ul>
        <p className="compliance-page__note">
          Need help publishing rules? See the{" "}
          <Link to="/how-to-run-a-sweepstakes">How to Run a Sweepstakes</Link>{" "}
          guide.
        </p>
      </section>

      <section className="compliance-page__section">
        <div className="compliance-page__header">
          <h2>Recordkeeping and audit trail</h2>
          <p>
            A sweepstakes platform should make it easy to track entries and
            prove outcomes.
          </p>
        </div>
        <ul className="compliance-page__list">
          <li>Export entrants and winners to CSV.</li>
          <li>Document selection criteria and timestamps.</li>
          <li>Store communication logs and notifications.</li>
          <li>Keep prize fulfillment records.</li>
        </ul>
      </section>

      <section className="compliance-page__cta">
        <h2>Run compliant sweepstakes with Clean Sweeps.</h2>
        <p>
          Clean Sweeps helps you manage entries, AMOE workflows, and winner
          selection from one Shopify sweepstakes platform.
        </p>
        <div className="compliance-page__actions">
          <a
            href="https://apps.shopify.com/clean-sweeps"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            Get the Shopify app
          </a>
          <Link to="/setup" className="btn btn--ghost">
            View setup guides
          </Link>
        </div>
      </section>
    </main>
  );
};

export default SweepstakesComplianceChecklist;
